import React from 'react';

import { PrimaryButton, ProButton } from '@components/Button/variants';
import EmailLeadCapture from '@components/EmailLeadCapture';
import SignInOption from '@components/Form/EmailForm/SignInOption';
import { Feature } from '@components/Paywall';
import useTrackEvent from '@hooks/useEventTracking';
import useWindowSize from '@hooks/useWindowSize';
import { Theme } from '@interfaces/global';
import { ClickTarget, ContentType, click } from '@tracking';
import { WIDTH_SM } from '@utils/constants';
import { FormLocation } from '@utils/enums';
import { getImgixSrc } from '@utils/images';
import { AuthoredContentReactToHtmlParser } from '@utils/reactHtmlParserTransform';

interface EnhancedPaywallProps {
  buttonText: string;
  features: Feature[];
  imagePath: string;
  isCurrentUserPresent: boolean;
  isPro: boolean;
  leadType: string;
  offerLink: string;
  projectSlug: string;
  subtitle: string;
  title: string;
}

const EnhancedPaywall = ({
  buttonText,
  features,
  imagePath,
  isCurrentUserPresent,
  isPro,
  leadType,
  offerLink,
  projectSlug,
  subtitle,
  title,
}: EnhancedPaywallProps) => {
  const { width: windowWidth } = useWindowSize();
  const Button = isPro ? ProButton : PrimaryButton;
  const { Track, trackEvent } = useTrackEvent({
    contentId: projectSlug,
    contentType: ContentType.Project,
  });

  return (
    <Track>
      <aside
        data-testid="paywall"
        className="-mx-16 flex flex-col overflow-hidden bg-white sm:-mx-28 md:min-h-[420px] md:flex-row md:flex-nowrap dark:bg-[var(--light-grayscale-charcoal)]"
      >
        <div className="flex w-full shrink-0 flex-col items-center justify-start bg-white px-24 py-32 sm:px-40 md:w-1/2 md:min-w-0 md:items-start md:px-48 md:py-40 lg:px-64 lg:py-48">
          {!isCurrentUserPresent && (
            <SignInOption
              className="mb-16 font-suisse-screen text-disclosure-sm"
              theme={Theme.Light}
            />
          )}
          <h2 className="my-0 max-w-[600px] text-center font-suisse-works text-headline-sm font-normal !text-primary-black md:text-left md:text-headline-md lg:text-headline-lg">
            {title}
          </h2>
          <p className="mb-0 mt-16 max-w-[460px] text-center font-suisse-intl text-body-lg text-grayscale-charcoal md:text-left">
            {subtitle}
          </p>
          <div className="mt-32 flex w-full flex-col items-center md:items-start">
            {isCurrentUserPresent ? (
              <Button
                as="link"
                href={offerLink}
                onClickCapture={() => trackEvent(click({ clickTarget: ClickTarget.Subscribe }))}
              >
                {buttonText}
              </Button>
            ) : (
              <EmailLeadCapture
                buttonText={buttonText}
                formLocation={FormLocation.ProjectPaywall}
                formClassName="flex justify-center md:justify-start"
                inputWrapperClassName="!w-full sm:!w-auto"
                inputClassName="min-w-full sm:min-w-[300px] dark:bg-[var(--light-grayscale-charcoal)]"
                isPro={isPro}
                isVertical={windowWidth < WIDTH_SM}
                leadType={leadType}
                redirectToOnSuccess={offerLink}
                withOauth
                oauthWrapperClassName="mt-24 flex w-full flex-wrap justify-center gap-16 md:justify-start sm:[&>:first-child]:order-last"
              />
            )}
          </div>
        </div>
        <div
          className="relative hidden min-h-[280px] w-full flex-col justify-center border-0 border-solid border-black bg-cover bg-right bg-no-repeat px-24 py-32 sm:px-40 md:flex md:min-h-0 md:w-1/2 md:min-w-0 md:py-48 lg:border-l lg:pl-48 lg:pr-32"
          style={{
            backgroundImage: `url(${getImgixSrc({ path: imagePath, isEnvDependent: false })})`,
          }}
        >
          <h2 className="mb-16 mt-0 font-suisse-intl-mono text-body-md font-normal text-primary-black">
            What you get
          </h2>
          <div className="grid max-w-[520px] grid-cols-1 gap-12 sm:grid-cols-2 sm:gap-16">
            {features.map((feature) => (
              <div className="bg-grayscale-beige p-16" key={feature.title}>
                <p className="m-0 font-suisse-screen text-disclosure-lg text-primary-black">
                  {AuthoredContentReactToHtmlParser(feature.title)} {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </aside>
    </Track>
  );
};

export default EnhancedPaywall;
